import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { AuthContext } from "./AuthContext";
import "../css/header.scss";

function Header() {
  const { cartItems, wishlist } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const results = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("en");
    if (!normalized) return [];
    return productData
      .filter((product) =>
        `${product.name} ${product.category} ${product.tags.join(" ")}`
          .toLocaleLowerCase("en")
          .includes(normalized)
      )
      .slice(0, 5);
  }, [query]);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
    setQuery("");
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.classList.toggle("nav-open", menuOpen || searchOpen);
    return () => document.body.classList.remove("nav-open");
  }, [menuOpen, searchOpen]);

  return (
    <header className="site-header">
      <div className="announcement-bar">
        <span>{siteConfig.announcement}</span>
      </div>

      <div className="header-main shell">
        <button
          className="icon-button header-main__menu"
          type="button"
          aria-label="Open menu"
          onClick={() => setMenuOpen(true)}
        >
          <FiMenu />
        </button>

        <button
          className="header-search-trigger"
          type="button"
          onClick={() => setSearchOpen(true)}
        >
          <FiSearch />
          <span>Search</span>
        </button>

        <Link
          className="wordmark"
          to="/"
          aria-label={`${siteConfig.displayName} - Home`}
        >
          <img src="/tastevani-logo.svg" alt={siteConfig.displayName} />
        </Link>

        <div className="header-actions">
          <Link
            className="icon-button account-indicator"
            to={isAuthenticated ? "/orders" : "/login"}
            aria-label="My account and orders"
          >
            <FiUser />
          </Link>
          <Link
            className="icon-button wishlist-indicator"
            to="/shop?wishlist=true"
            aria-label="Wishlist"
          >
            <FiHeart />
            {wishlist.length > 0 && <span>{wishlist.length}</span>}
          </Link>
          <Link
            className="icon-button cart-indicator"
            to="/cartpage"
            aria-label={`Cart, ${cartCount} item(s)`}
          >
            <FiShoppingBag />
            <span>{cartCount}</span>
          </Link>
        </div>
      </div>

      <nav className="desktop-nav" aria-label="Main navigation">
        <div className="shell desktop-nav__inner">
          <NavLink to="/shop">Shoes & Boots</NavLink>
          <NavLink to="/shop?search=sneakers">Casual Styles</NavLink>
          <NavLink to="/shop?sort=new">New Arrivals</NavLink>
          <NavLink className="desktop-nav__sale" to="/shop?sort=sale">
            Last Chance
          </NavLink>
          <NavLink to="/about">Our Story</NavLink>
          <NavLink to="/contact">Contact</NavLink>
          <div className={`desktop-nav__more ${moreOpen ? "is-open" : ""}`}>
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-haspopup="true"
              onClick={() => setMoreOpen((open) => !open)}
            >
              More <FiChevronDown />
            </button>
            <div className="desktop-nav__more-menu">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/cookies">Cookie Policy</Link>
              <Link to="/terms">Terms & Legal Notice</Link>
              <Link to="/faqs">FAQs</Link>
              <Link to="/shipping">Delivery</Link>
              <Link to="/returns">Returns & Exchanges</Link>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`drawer-overlay ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
        onClick={() => setMenuOpen(false)}
      />
      <aside
        className={`menu-drawer ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="drawer-heading">
          <span>Menu</span>
          <button
            className="icon-button"
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <FiX />
          </button>
        </div>
        <nav>
          <Link to="/shop">View All</Link>
          <Link to="/shop">Shoes & Boots</Link>
          <Link to="/shop?search=sneakers">Casual Styles</Link>
          <Link to="/shop?search=loafer">Smart Styles</Link>
          <Link to="/shop?search=boot">Boots</Link>
          <Link to="/shop?search=sandal">Sandals</Link>
          <Link className="menu-drawer__emphasis" to="/shop?sort=sale">
            Last Chance
          </Link>
        </nav>
        <div className="menu-drawer__secondary">
          <Link to={isAuthenticated ? "/orders" : "/login"}>
            {isAuthenticated ? "My orders" : "Account login"}
          </Link>
          {isAuthenticated && <Link to="/customers">Customers</Link>}
          <Link to="/about">Our Story</Link>
          <Link to="/faqs">FAQ</Link>
          <Link to="/contact">Contact Us</Link>
          <details>
            <summary>More</summary>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/cookies">Cookie Policy</Link>
            <Link to="/terms">Terms & Legal Notice</Link>
            <Link to="/shipping">Delivery</Link>
            <Link to="/returns">Returns & Exchanges</Link>
          </details>
        </div>
      </aside>

      <div
        className={`search-panel ${searchOpen ? "is-open" : ""}`}
        aria-hidden={!searchOpen}
      >
        <div className="search-panel__bar shell">
          <FiSearch />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search for a product..."
            aria-label="Search for a product"
            autoFocus={searchOpen}
          />
          <button
            type="button"
            className="icon-button"
            aria-label="Close search"
            onClick={() => setSearchOpen(false)}
          >
            <FiX />
          </button>
        </div>
        <div className="search-panel__results shell">
          {!query && <p>Try “sneakers”, “loafers” or “boots”.</p>}
          {query && results.length === 0 && (
            <p>No products match your search.</p>
          )}
          {results.map((product) => (
            <Link key={product.id} to={`/product/${product.id}`}>
              <img src={product.images[0]} alt="" />
              <span>
                <strong>{product.name}</strong>
                <small>{product.category}</small>
              </span>
              <b>{formatPrice(product.price)}</b>
            </Link>
          ))}
          {results.length > 0 && (
            <Link
              className="search-panel__all"
              to={`/shop?search=${encodeURIComponent(query)}`}
            >
              View all results
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
